/* eslint-disable import/export */
import * as React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import { FlameTheme } from '@lightspeed/flame/Core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import forEach from 'lodash/forEach';

interface ReactRendererProps<Props> {
  component: React.ComponentType<Props> | React.FC<Props>;
  selector: string;
  store?: typeof import('pages/store').default;
  props?: Props;
  callback?: () => void;
}

const devToolsButtonClassName = 'react-query-dev-tools-toggle-button';
let queryClient: any;

if (!queryClient) {
  queryClient = new QueryClient();
}

export function getReactNodes(selector: string): NodeListOf<HTMLElement>;
export function getReactNodes(selector: HTMLElement): HTMLElement;
export function getReactNodes(selector: string | HTMLElement) {
  if (typeof selector === 'string') {
    return document.querySelectorAll(selector);
  }
  return selector;
}

type Selector = string | HTMLElement;

export function getReactNode(selector: Selector) {
  if (typeof selector === 'string') {
    return document.querySelector(selector);
  }
  return selector;
}

export function getReactProps(selector: Selector, dataAttributeName: string = 'data-react-props') {
  const node = getReactNode(selector);
  if (!(node instanceof HTMLElement)) return {};

  const props = node.getAttribute(dataAttributeName) || {};

  return typeof props === 'string' ? JSON.parse(props) : props;
}

const hasReactQueryDevtools = () =>
  document.querySelectorAll(`.${devToolsButtonClassName}`).length > 0;

export function createReactRenderer<Props extends Record<string, any> = Record<string, any>>({
  component: Component,
  selector,
  store,
  props: createProps,
  callback: createCallback = () => {},
}: ReactRendererProps<Props>) {
  const nodes: HTMLElement[] = [];

  function mount({ props, callback = () => {} }: { props?: Props; callback?: () => void } = {}) {
    const newNodes = getReactNodes(selector);

    if (!newNodes.length) return;

    const mountedNodes: React.ReactElement[] = [];

    forEach(newNodes, (newNode) => {
      // If already mounted, ignore the new mount request
      if (nodes.some((node) => newNode === node)) return;

      nodes.push(newNode);

      const renderProps = Object.assign(
        getReactProps(newNode),
        createProps,
        {
          // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
          renderedComponent: this,
        },
        props || {}
      );

      const component: React.ReactElement<Props> = <Component {...renderProps} />;

      const WrappedComponent = () => (
        <QueryClientProvider client={queryClient}>
          <FlameTheme>{component}</FlameTheme>
          {!hasReactQueryDevtools() ? (
            <ReactQueryDevtools
              initialIsOpen={false}
              position="bottom-right"
              toggleButtonProps={{ className: devToolsButtonClassName }}
            />
          ) : null}
        </QueryClientProvider>
      );

      const renderedComponent = store ? (
        <Provider store={store}>
          <WrappedComponent />
        </Provider>
      ) : (
        <WrappedComponent />
      );

      ReactDOM.render(renderedComponent, newNode, () => {
        callback();
        createCallback();
      });

      mountedNodes.push(component);
    });

    // eslint-disable-next-line consistent-return
    return mountedNodes;
  }

  function unmount() {
    nodes.forEach(ReactDOM.unmountComponentAtNode);
    nodes.length = 0;
  }

  return { nodes, mount, unmount };
}
